import type {
	BulkDeletePreview,
	DeletePreview,
	RestorePreview,
	RestoreResult,
	SoftDeleteEntityType,
} from '@/common/soft-delete';
import { axios } from '@/lib/utils/axios';

import { API_URLS } from '../query.client';

/** One item of the soft-delete API: its type and its REST path (GH #331). */
export type SoftDeleteTarget = {
	type: Exclude<SoftDeleteEntityType, 'WORK_PACKAGE_ITEM' | 'POLYGON'>;
	/** The item's id: queries about it are not refetched right after it is deleted or restored */
	id: string;
	path: string;
};

export const softDeleteTargets = {
	asset: (id: string): SoftDeleteTarget => ({ type: 'ASSET', id, path: `${API_URLS.ASSETS.INDEX}/${id}` }),
	subAsset: (id: string): SoftDeleteTarget => ({ type: 'SUB_ASSET', id, path: `${API_URLS.SUB_ASSETS.INDEX}/${id}` }),
	inspection: (assetId: string, id: string): SoftDeleteTarget => ({
		type: 'INSPECTION',
		id,
		path: `${API_URLS.INSPECTION.INDEX(assetId)}/${id}`,
	}),
	damage: (id: string): SoftDeleteTarget => ({ type: 'DAMAGE', id, path: `${API_URLS.DAMAGES.INDEX}/${id}` }),
	dataUpload: (id: string): SoftDeleteTarget => ({
		type: 'DATA_UPLOAD',
		id,
		path: `${API_URLS.DATA_UPLOAD.INDEX}/${id}`,
	}),
	workPackage: (id: string): SoftDeleteTarget => ({
		type: 'WORK_PACKAGE',
		id,
		path: `${API_URLS.WORK_PACKAGES.INDEX}/${id}`,
	}),
};

export async function getDeletePreview(target: SoftDeleteTarget): Promise<DeletePreview> {
	const response = await axios.get<DeletePreview>(`${target.path}/delete-preview`);
	return response.data;
}

/** Delete previews of the damages selected for a bulk delete, in one request. */
export async function getBulkDamageDeletePreview(ids: string[]): Promise<BulkDeletePreview> {
	const response = await axios.post<BulkDeletePreview>(`${API_URLS.DAMAGES.INDEX}/delete-preview`, { ids });
	return response.data;
}

/** Deletes the item and everything below it. `acceptWorkPackageImpact` confirms changes to work packages. */
export async function softDeleteItem(
	target: SoftDeleteTarget,
	{ acceptWorkPackageImpact = false }: { acceptWorkPackageImpact?: boolean } = {},
): Promise<void> {
	await axios.delete(target.path, { params: acceptWorkPackageImpact ? { acceptWorkPackageImpact: true } : undefined });
}

export async function getRestorePreview(target: SoftDeleteTarget): Promise<RestorePreview> {
	const response = await axios.get<RestorePreview>(`${target.path}/restore-preview`);
	return response.data;
}

/** Brings back the item and everything deleted together with it. */
export async function restoreItem(target: SoftDeleteTarget): Promise<RestoreResult> {
	const response = await axios.post<RestoreResult>(`${target.path}/restore`);
	return response.data;
}
